import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCareCreditIntegrationV1Service, serviceName } from '../service';
import { GetPrequalificationOfferStatusIO, ServiceQueries } from '../types';
import { useCareCreditIntegrationV1Queries } from './use-care-credit-integration-v1-queries';
import {
  UseCareCreditIntegrationV1QueryEndpointArgs,
  useCareCreditIntegrationV1Query,
} from './use-care-credit-integration-v1-query';

/**
 * A hook that returns a query for the `GetPrequalificationOfferStatus` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPrequalificationOfferStatusQuery = <E = unknown, D = GetPrequalificationOfferStatusIO['output']>(
  args: UseCareCreditIntegrationV1QueryEndpointArgs<'GetPrequalificationOfferStatus', E, D>
) =>
  useCareCreditIntegrationV1Query({
    endpointName: 'GetPrequalificationOfferStatus',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetPrequalificationOfferStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPrequalificationOfferStatusQueries = <E = unknown, D = GetPrequalificationOfferStatusIO['output']>(
  argsArr: UseCareCreditIntegrationV1QueryEndpointArgs<'GetPrequalificationOfferStatus', E, D>[]
) =>
  useCareCreditIntegrationV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetPrequalificationOfferStatus',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetPrequalificationOfferStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetPrequalificationOfferStatus = () => {
  const queryClient = useQueryClient();

  const fetchGetPrequalificationOfferStatus = async <E = unknown, D = GetPrequalificationOfferStatusIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCareCreditIntegrationV1QueryEndpointArgs<'GetPrequalificationOfferStatus', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetPrequalificationOfferStatusIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetPrequalificationOfferStatus'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetPrequalificationOfferStatusIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetPrequalificationOfferStatus'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetPrequalificationOfferStatus'>({
          serviceName,
          endpointName: 'GetPrequalificationOfferStatus',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCareCreditIntegrationV1Service.GetPrequalificationOfferStatus(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetPrequalificationOfferStatus;
};
